// shader.js – the scripts/*.shader files, as much of them as a software
// painter needs: which picture a surface shows (the first stage that is not
// the lightmap), whether it blends additively or by alpha, animates, scrolls
// or ripples, is two-sided, and for a sky which cloud layers to draw.

const BLEND = {
  'GL_ONE,GL_ZERO': 'opaque', 'GL_ONE,GL_ONE': 'add', 'GL_SRC_ALPHA,GL_ONE_MINUS_SRC_ALPHA': 'blend', 'GL_DST_COLOR,GL_ZERO': 'filter',
  'GL_ZERO,GL_SRC_COLOR': 'filter', 'GL_ZERO,GL_ONE_MINUS_SRC_COLOR': 'filterinv', 'GL_SRC_ALPHA,GL_ONE': 'add', 'GL_DST_COLOR,GL_SRC_ALPHA': 'filter',
  'GL_DST_COLOR,GL_ONE': 'filteradd', 'GL_ONE,GL_ONE_MINUS_SRC_ALPHA': 'premul',
};

const BLEND_WORDS = { ADD: ['GL_ONE', 'GL_ONE'], FILTER: ['GL_DST_COLOR', 'GL_ZERO'], BLEND: ['GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA'] };

function tokenize(text) {
  const out = [];
  const re = /\/\/[^\n]*|\/\*[\s\S]*?\*\/|"([^"]*)"|[{}]|[^\s{}"]+/g;
  let m;
  while ((m = re.exec(text))) {
    if (m[0].startsWith('//') || m[0].startsWith('/*')) continue;
    out.push(m[1] !== undefined ? m[1] : m[0]);
  }
  return out;
}

/** Parse one script into shaders: name → { stages, surfaceparms, sky, cull, ... } */
export function parseShaderScript(text, into = new Map()) {
  const t = tokenize(text);
  let i = 0;
  while (i < t.length) {
    const name = t[i++].toLowerCase();
    if (t[i] !== '{') continue;
    i++;
    const sh = { name, stages: [], parms: new Set(), cull: 'front', sky: null, sort: 0, deform: null, deforms: [], nopicmip: false };
    while (i < t.length && t[i] !== '}') {
      if (t[i] === '{') {
        i++;
        const st = { map: null, anim: null, animFps: 0, blend: 'opaque', src: 'GL_ONE', dst: 'GL_ZERO', alphaFunc: null, scroll: null, scale: null, turb: null, rotate: 0, clamp: false, lightmap: false, rgbGen: null, depthWrite: false, tcGen: null, tcMods: [] };
        while (i < t.length && t[i] !== '}') {
          const key = t[i++].toLowerCase();
          const args = [];
          while (i < t.length && t[i] !== '}' && t[i] !== '{' && !isStageKeyword(t[i])) args.push(t[i++]);
          switch (key) {
            case 'map': case 'clampmap': {
              const m = (args[0] ?? '').toLowerCase().replace(/\\/g, '/');
              if (m === '$lightmap') st.lightmap = true; else if (m !== '$whiteimage') st.map = m;
              if (key === 'clampmap') st.clamp = true;
              break;
            }
            case 'animmap': st.animFps = Number(args[0]) || 10; st.anim = args.slice(1).map((a) => a.toLowerCase().replace(/\\/g, '/')); if (!st.map) st.map = st.anim[0]; break;
            case 'blendfunc': {
              const a = args.map((x) => x.toUpperCase());
              if (a.length === 1) st.blend = a[0] === 'ADD' ? 'add' : a[0] === 'FILTER' ? 'filter' : a[0] === 'BLEND' ? 'blend' : 'opaque';
              else st.blend = BLEND[`${a[0]},${a[1]}`] ?? (a[1] === 'GL_ONE' ? 'add' : 'blend');
              // the factors themselves, for the painter that draws every stage (NameToSrcBlendMode)
              [st.src, st.dst] = a.length === 1 ? (BLEND_WORDS[a[0]] ?? ['GL_ONE', 'GL_ZERO']) : [a[0], a[1]];
              break;
            }
            case 'alphafunc': st.alphaFunc = (args[0] ?? '').toUpperCase(); break;
            case 'tcmod': {
              const k = (args[0] ?? '').toLowerCase();
              st.tcMods.push([k, ...args.slice(1).map((a) => a.toLowerCase())]);   // in the script's order (ComputeTexCoords)
              if (k === 'scroll') st.scroll = [Number(args[1]) || 0, Number(args[2]) || 0];
              else if (k === 'scale') st.scale = [Number(args[1]) || 1, Number(args[2]) || 1];
              else if (k === 'turb') st.turb = [Number(args[1]) || 0, Number(args[2]) || 0, Number(args[3]) || 0, Number(args[4]) || 0];
              else if (k === 'rotate') st.rotate = Number(args[1]) || 0;
              break;
            }
            case 'tcgen': st.tcGen = (args[0] ?? '').toLowerCase(); break;
            case 'rgbgen': st.rgbGen = args.map((x) => x.toLowerCase()); break;
            case 'depthwrite': st.depthWrite = true; break;
            case 'alphagen': st.alphaGen = args.map((x) => x.toLowerCase()); break;
            default: break;
          }
        }
        i++;
        sh.stages.push(st);
        continue;
      }
      const key = t[i++].toLowerCase();
      const args = [];
      while (i < t.length && t[i] !== '}' && t[i] !== '{' && !isShaderKeyword(t[i])) args.push(t[i++]);
      switch (key) {
        case 'surfaceparm': sh.parms.add((args[0] ?? '').toLowerCase()); break;
        case 'cull': { const c = (args[0] ?? 'front').toLowerCase(); sh.cull = c === 'none' || c === 'disable' || c === 'twosided' ? 'none' : c === 'back' || c === 'backside' || c === 'backsided' ? 'back' : 'front'; break; }
        case 'skyparms': sh.sky = { box: args[0] && args[0] !== '-' ? args[0].toLowerCase() : null, height: Number(args[1]) || 512 }; break;
        case 'sort': sh.sort = args[0]; break;
        case 'deformvertexes': sh.deform = args.map((x) => x.toLowerCase()); sh.deforms.push(sh.deform); break;
        case 'fogparms': { const n = args.filter((a) => a !== '(' && a !== ')').map(Number); if (n.length >= 4) sh.fog = { color: n.slice(0, 3), opaque: n[3] || 1 }; break; }
        case 'nopicmip': case 'nomipmaps': sh.nopicmip = true; break;
        case 'portal': sh.portal = true; break;
        default: break;
      }
    }
    i++;
    into.set(name, sh);
  }
  return into;
}

const STAGE_KEYS = new Set(['map', 'clampmap', 'animmap', 'blendfunc', 'alphafunc', 'tcmod', 'tcgen', 'rgbgen', 'alphagen', 'depthwrite', 'depthfunc', 'detail', 'videomap']);
const SHADER_KEYS = new Set(['fogparms', 'surfaceparm', 'cull', 'skyparms', 'sort', 'deformvertexes', 'nopicmip', 'nomipmaps', 'polygonoffset', 'entitymergable', 'fogparms', 'light', 'tesssize', 'q3map_sun', 'q3map_surfacelight', 'qer_editorimage', 'qer_trans', 'qer_nocarve', 'q3map_lightimage', 'q3map_globaltexture', 'q3map_lightsubdivide', 'cloudparms', 'sky', 'portal', 'fogonly', 'q3map_backshader', 'q3map_flare', 'q3map_tessSize', 'q3map_backsplash', 'q3map_lightmapsamplesize', 'q3map_novertexshadows', 'q3map_forcesunlight', 'q3map_vertexshadows', 'q3map_tesssize', 'lightning', 'entitymergable']);
const isStageKeyword = (s) => STAGE_KEYS.has(s.toLowerCase());
const isShaderKeyword = (s) => SHADER_KEYS.has(s.toLowerCase());

/**
 * deformVertexes wave <div> <func> <base> <amp> <phase> <freq> (along the normal, the phase spread over the
 * vertex's x + y + z by 1 / div) and move <x> <y> <z> <func> <base> <amp> <phase> <freq> (along the vector),
 * as RB_DeformTessGeometry has them; the others are not drawn
 */
const WAVE_FUNCS = { sin: 0, triangle: 1, square: 2, sawtooth: 3, inversesawtooth: 4 };
export function parseDeform(d) {
  const num = (k) => Number(d[k]) || 0;
  if (d[0] === 'wave' && d.length >= 7 && d[2] in WAVE_FUNCS) {
    const div = num(1);
    return { kind: 1, spread: div ? 1 / div : 100, func: WAVE_FUNCS[d[2]], base: num(3), amp: num(4), phase: num(5), freq: num(6), move: [0, 0, 0] };
  }
  if (d[0] === 'move' && d.length >= 9 && d[4] in WAVE_FUNCS) {
    return { kind: 2, spread: 0, func: WAVE_FUNCS[d[4]], base: num(5), amp: num(6), phase: num(7), freq: num(8), move: [num(1), num(2), num(3)] };
  }
  return null;
}

/** EvalWaveForm: base + amp × the function's value at phase + time × freq (one period per unit) */
export function waveValue(func, base, amp, phase, freq, time) {
  let x = phase + time * freq;
  x -= Math.floor(x);
  const v = func === 0 ? Math.sin(x * 2 * Math.PI) : func === 1 ? (x < 0.25 ? x * 4 : x < 0.75 ? 2 - x * 4 : x * 4 - 4) : func === 2 ? (x < 0.5 ? 1 : -1) : func === 3 ? x : 1 - x;
  return base + amp * v;
}

/** A vertex moved by a look's deforms at a time: [x, y, z] from [x, y, z] and its normal */
export function deformVertex(deforms, x, y, z, nx, ny, nz, time) {
  for (const d of deforms) {
    if (d.kind === 1) {
      const s = waveValue(d.func, d.base, d.amp, d.phase + (x + y + z) * d.spread, d.freq, time);
      x += nx * s; y += ny * s; z += nz * s;
    } else {
      const s = waveValue(d.func, d.base, d.amp, d.phase, d.freq, time);
      x += d.move[0] * s; y += d.move[1] * s; z += d.move[2] * s;
    }
  }
  return [x, y, z];
}

/** RB_CalcEnvironmentTexCoords: the view reflected in the surface, its y and z as s and t */
export function envTexCoords(px, py, pz, nx, ny, nz, ex, ey, ez) {
  let vx = ex - px, vy = ey - py, vz = ez - pz;
  const l = Math.hypot(vx, vy, vz) || 1;
  vx /= l; vy /= l; vz /= l;
  const d = nx * vx + ny * vy + nz * vz;
  return [0.5 + (ny * 2 * d - vy) * 0.5, 0.5 - (nz * 2 * d - vz) * 0.5];
}

/**
 * A powerup's shell over an MD3 surface (CG_AddRefEntityWithPowerups' customShader): each vertex moved out
 * along its normal by the shader's deforms, its texture the eye reflected in it (tcGen environment) through
 * the stage's tcMods turb, rotate and scroll, in that order (RB_CalcTurbulentTexCoords,
 * RB_CalcRotateTexCoords, RB_CalcScrollTexCoords). All in the model's frame: `eye` is the view origin there.
 * Returns { xyz, st } (three and two floats a vertex).
 */
export function shellMesh(surf, frame, eye, look, time) {
  const nv = surf.numVerts, base = frame * nv * 4, src = surf.xyz;
  const xyz = new Float32Array(nv * 3), st = new Float32Array(nv * 2);
  const k = 2 * Math.PI / 255, TAU = 2 * Math.PI;
  let rc = 1, rs = 0;
  if (look.rotate) { const a = (-look.rotate * time * Math.PI) / 180; rc = Math.cos(a); rs = Math.sin(a); }
  const ss = look.scroll ? look.scroll[0] * time - Math.floor(look.scroll[0] * time) : 0;
  const ts = look.scroll ? look.scroll[1] * time - Math.floor(look.scroll[1] * time) : 0;
  for (let i = 0; i < nv; i++) {
    const p = base + i * 4;
    const packed = src[p + 3] & 0xffff, lat = ((packed >> 8) & 255) * k, lng = (packed & 255) * k;
    const nx = Math.cos(lat) * Math.sin(lng), ny = Math.sin(lat) * Math.sin(lng), nz = Math.cos(lng);
    let x = src[p] / 64, y = src[p + 1] / 64, z = src[p + 2] / 64;
    if (look.deforms) [x, y, z] = deformVertex(look.deforms, x, y, z, nx, ny, nz, time);
    let [s, t] = envTexCoords(x, y, z, nx, ny, nz, eye[0], eye[1], eye[2]);
    if (look.turb) {
      const amp = look.turb[1], now = look.turb[2] + time * look.turb[3];
      s += Math.sin(((x + z) / 128 * 0.125 + now) * TAU) * amp;
      t += Math.sin((y / 128 * 0.125 + now) * TAU) * amp;
    }
    if (look.rotate) {
      const s2 = s * rc - t * rs + 0.5 - 0.5 * rc + 0.5 * rs;
      t = s * rs + t * rc + 0.5 - 0.5 * rs - 0.5 * rc;
      s = s2;
    }
    xyz[i * 3] = x; xyz[i * 3 + 1] = y; xyz[i * 3 + 2] = z;
    st[i * 2] = s + ss; st[i * 2 + 1] = t + ts;
  }
  return { xyz, st };
}

/** The eye in a model's frame (axis: the rows forward, left, up) */
export function eyeInModel(view, origin, axis) {
  const ex = view.x - origin[0], ey = view.y - origin[1], ez = view.z - origin[2];
  return [ex * axis[0] + ey * axis[1] + ez * axis[2], ex * axis[3] + ey * axis[4] + ez * axis[5], ex * axis[6] + ey * axis[7] + ez * axis[8]];
}

/** Every scripts/*.shader of the pak, parsed. */
export function loadShaders(pak) {
  const shaders = new Map();
  for (const f of pak.list('scripts/', '.shader')) {
    try { parseShaderScript(pak.text(f), shaders); } catch (e) { console.warn(`${f}: ${e.message}`); }
  }
  return shaders;
}

/**
 * What to draw for a texture name: the picture, blend mode and animation of
 * the stage that carries the surface's colour. Without a script the texture
 * is the picture of the same name, lightmapped.
 */
export function surfaceLook(shaders, name) {
  const sh = shaders.get(name.toLowerCase());
  const look = { name, image: null, anim: null, animFps: 0, blend: 'opaque', lightmapped: true, scroll: null, scale: null, turb: null, twoSided: false, sky: null, alphaTest: false, nodraw: false, rotate: 0, tcGen: null, vertexColor: false, add: null, env: null, autosprite: false, deforms: null };
  if (!sh) { look.image = name; return look; }
  look.autosprite = sh.deforms.some((d) => d[0] === 'autosprite' || d[0] === 'autosprite2');
  look.portal = !!sh.portal;
  // alphaGen portal [range]: the view fades into the stage over that many units (256); without it, never
  if (look.portal) {
    const ag = sh.stages.find((s) => s.alphaGen?.[0] === 'portal');
    look.portalRange = ag ? Number(ag.alphaGen[1]) || 256 : 0;
  }
  const deforms = sh.deforms.map(parseDeform).filter(Boolean);
  if (deforms.length) look.deforms = deforms.slice(0, 2);
  look.twoSided = sh.cull === 'none';
  // a fog volume's surface with no stages of its own shows only the fog of what is under it
  look.nodraw = sh.parms.has('nodraw') || (sh.parms.has('fog') && !sh.stages.some((s) => s.map));
  if (sh.parms.has('sky') || sh.sky) {
    look.sky = { box: sh.sky?.box ?? null, layers: sh.stages.filter((s) => s.map).map((s) => ({ image: s.map, scroll: s.scroll, scale: s.scale, blend: s.blend })) };
    look.lightmapped = false;
    return look;
  }
  const hasLightmap = sh.stages.some((s) => s.lightmap);
  // the colour stage: the first stage with a picture that is not purely a filter over the lightmap; the
  // stages after it that add (glowing lights) are remembered as the `add` layer
  let main = null, over = false;
  for (const s of sh.stages) {
    if (!s.map) continue;
    if (!main) { main = s; continue; }
    // a picture with holes blended over an opaque layer (skullarch_b over its scrolling fire): the picture is
    // what a single-look painter shows
    if (!over && main.blend === 'opaque' && !main.tcGen && s.blend === 'blend' && !s.tcGen && !look.add) { main = s; over = true; continue; }
    // a chrome stage (tcGen environment) the main picture is blended over: the picture is the one on top
    // and the chrome shows where its alpha is low (pewter_shiney)
    if (main.tcGen === 'environment' && !look.env && s.blend === 'blend') { look.env = { image: main.map, mode: 'under' }; main = s; continue; }
    // a chrome stage added over the picture (largerblock3blood's tinfx3)
    if (!look.env && s.tcGen === 'environment' && (s.blend === 'add' || s.blend === 'filteradd')) { look.env = { image: s.map, mode: 'add' }; continue; }
    if (!look.add && (s.blend === 'add' || s.blend === 'filteradd') && s.map && !s.tcGen) look.add = { image: s.map, anim: s.anim, animFps: s.animFps, scroll: s.scroll, scale: s.scale, rotate: s.rotate };
  }
  if (!main) { look.image = sh.stages.length ? null : name; look.lightmapped = hasLightmap || !sh.parms.has('nolightmap'); if (!look.image && !hasLightmap) look.nodraw = look.nodraw || sh.stages.length === 0 ? look.nodraw : false; return look; }
  look.image = main.map;
  look.anim = main.anim; look.animFps = main.animFps;
  look.scroll = main.scroll; look.scale = main.scale; look.turb = main.turb; look.rotate = main.rotate; look.tcGen = main.tcGen;
  look.alphaTest = !!main.alphaFunc;
  look.vertexColor = !!(main.rgbGen && (main.rgbGen[0] === 'vertex' || main.rgbGen[0] === 'exactvertex'));
  // lit by the lightmap when a $lightmap stage multiplies it, or when the shader never said nolightmap
  look.lightmapped = hasLightmap || (!sh.parms.has('nolightmap') && main.blend === 'opaque' && !look.vertexColor);
  const firstIsLightmap = sh.stages[0]?.lightmap;
  if (main.blend === 'filter' && (firstIsLightmap || hasLightmap)) look.blend = 'opaque';
  else if (main.blend === 'add' && !firstIsLightmap) look.blend = 'add';
  else if (main.blend === 'blend' || main.blend === 'premul') look.blend = 'blend';
  else if (main.blend === 'filter' || main.blend === 'filterinv') look.blend = 'filter';
  else look.blend = 'opaque';
  if (sh.parms.has('trans') && look.blend === 'opaque' && main.alphaFunc) look.blend = 'opaque';
  if (look.env?.mode === 'under') look.blend = 'opaque';   // the chrome fills what the picture's alpha leaves
  if (over) look.blend = 'opaque';
  // what this look leaves out (a stage it drops, a stage's rgbGen wave, the added glow's own wave): the WebGL
  // painter then draws the shader's stages one by one, as RB_StageIteratorGeneric does
  if (!look.portal && !look.autosprite && !look.env && !look.tcGen) {
    const shown = new Set([main.map, look.add?.image].filter(Boolean));
    const lossy = !!look.add || over || sh.stages.some((s) => (s.map && !s.lightmap && !shown.has(s.map)) || s.rgbGen?.[0] === 'wave' || s.alphaGen?.[0] === 'lightingspecular');
    if (lossy) {
      look.stages = shaderStages(sh);
      const first = look.stages[0];
      look.stagesOpaque = !!first && first.src === 'GL_ONE' && first.dst === 'GL_ZERO';
    }
  }
  return look;
}

/**
 * A shader's stages as the WebGL painter draws them, each a pass over the surface: the picture (or the
 * lightmap, or the view reflected for tcGen environment), its colour (identity, a wave, the vertex light),
 * its texture motion, its alpha test and its blend factors. The first stage of an opaque shader writes the
 * depth; the others only blend, unless the script says depthWrite.
 */
export function shaderStages(sh) {
  const out = [];
  for (const s of sh.stages) {
    if (!s.map && !s.lightmap) continue;
    const g = s.rgbGen ?? [];
    let rgb = { kind: 'identity' };
    if (g[0] === 'wave' && g[1] in WAVE_FUNCS) rgb = { kind: 'wave', func: WAVE_FUNCS[g[1]], base: Number(g[2]) || 0, amp: Number(g[3]) || 0, phase: Number(g[4]) || 0, freq: Number(g[5]) || 0 };
    else if (g[0] === 'vertex' || g[0] === 'exactvertex') rgb = { kind: 'vertex' };
    else if (g[0] === 'const') {
      const c = g.slice(1).map(Number).filter(Number.isFinite);
      if (c.length >= 3) rgb = { kind: 'const', color: c.slice(0, 3).map((v) => Math.min(1, Math.max(0, v))) };
    }
    // alphaGen: const, wave, portal (the distance over its range); the others leave the picture's alpha
    const ag = s.alphaGen ?? [];
    let alpha = null;
    if (ag[0] === 'const' && Number.isFinite(Number(ag[1]))) alpha = { kind: 'const', value: Number(ag[1]) };
    else if (ag[0] === 'wave' && ag[1] in WAVE_FUNCS) alpha = { kind: 'wave', func: WAVE_FUNCS[ag[1]], base: Number(ag[2]) || 0, amp: Number(ag[3]) || 0, phase: Number(ag[4]) || 0, freq: Number(ag[5]) || 0 };
    else if (ag[0] === 'portal') alpha = { kind: 'portal', range: Number(ag[1]) || 256 };
    else if (ag[0] === 'lightingspecular') alpha = { kind: 'specular' };
    out.push({
      image: s.lightmap ? null : s.map, lightmap: s.lightmap, anim: s.anim, animFps: s.animFps,
      src: s.src, dst: s.dst, alphaFunc: s.alphaFunc, depthWrite: s.depthWrite,
      scroll: s.scroll, scale: s.scale, turb: s.turb, rotate: s.rotate, tcMods: s.tcMods, tcGen: s.tcGen, rgb, alpha,
    });
  }
  return out;
}

/**
 * ComputeTexCoords' tcMods for a stage at a time, in the script's order, as one affine map of the texture
 * coordinates: [m00, m10, m20, m01, m11, m21], s' = s m00 + t m10 + m20, t' = s m01 + t m11 + m21
 * (RB_CalcTransformTexCoords' matrix). scroll, scale, rotate about the middle, stretch about the middle by
 * 1 / its wave, transform; turb is per vertex and is the painter's (left out here).
 */
export function stageTcMatrix(stage, time) {
  let m = [1, 0, 0, 0, 1, 0];
  const then = (o) => {   // o after m
    m = [o[0] * m[0] + o[1] * m[3], o[0] * m[1] + o[1] * m[4], o[0] * m[2] + o[1] * m[5] + o[2],
      o[3] * m[0] + o[4] * m[3], o[3] * m[1] + o[4] * m[4], o[3] * m[2] + o[4] * m[5] + o[5]];
  };
  const n = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
  for (const [k, ...a] of stage.tcMods ?? []) {
    if (k === 'scroll') then([1, 0, n(a[0]) * time, 0, 1, n(a[1]) * time]);
    else if (k === 'scale') then([n(a[0], 1), 0, 0, 0, n(a[1], 1), 0]);
    else if (k === 'rotate') {
      const r = (-n(a[0]) * time * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
      then([c, -s, 0.5 - 0.5 * c + 0.5 * s, s, c, 0.5 - 0.5 * s - 0.5 * c]);
    } else if (k === 'stretch' && a[0] in WAVE_FUNCS) {
      const w = waveValue(WAVE_FUNCS[a[0]], n(a[1]), n(a[2]), n(a[3]), n(a[4]), time);
      const p = w ? 1 / w : 1;
      then([p, 0, 0.5 - 0.5 * p, 0, p, 0.5 - 0.5 * p]);
    } else if (k === 'transform') then([n(a[0], 1), n(a[2]), n(a[4]), n(a[1]), n(a[3], 1), n(a[5])]);
  }
  return m;
}

/** A stage's colour at a time, [r, g, b]: rgbGen wave clamped to 0..1 (RB_CalcWaveColor), const, else 1 */
export function stageColor(rgb, time) {
  if (rgb.kind === 'const') return rgb.color;
  const v = stageBrightness(rgb, time);
  return [v, v, v];
}

/** RB_CalcSpecularAlpha's light: one fixed point for every surface (the source's "FIXME: track dynamics") */
export const SPECULAR_LIGHT = [-960, 1980, 96];

/**
 * alphaGen lightingSpecular at a vertex (RB_CalcSpecularAlpha): the light's direction reflected in the
 * normal, against the direction to the eye, to the fourth power, as alpha 0..1. The WebGL painter's world
 * shader does the same per vertex (vSpec), in the model's own space as the source does.
 */
export function specularAlpha(normal, pos, eye) {
  const L = [SPECULAR_LIGHT[0] - pos[0], SPECULAR_LIGHT[1] - pos[1], SPECULAR_LIGHT[2] - pos[2]];
  const ll = Math.hypot(L[0], L[1], L[2]) || 1;
  L[0] /= ll; L[1] /= ll; L[2] /= ll;
  const d = normal[0] * L[0] + normal[1] * L[1] + normal[2] * L[2];
  const r = [normal[0] * 2 * d - L[0], normal[1] * 2 * d - L[1], normal[2] * 2 * d - L[2]];
  const v = [eye[0] - pos[0], eye[1] - pos[1], eye[2] - pos[2]];
  const l = (r[0] * v[0] + r[1] * v[1] + r[2] * v[2]) / (Math.hypot(v[0], v[1], v[2]) || 1);
  return l < 0 ? 0 : Math.min(1, l * l * l * l);
}

/** A stage's alpha source: alphaGen const and wave (RB_CalcWaveAlpha) as a number, portal as its range */
export function stageAlpha(alpha, time) {
  if (!alpha) return null;
  if (alpha.kind === 'const') return alpha.value;
  if (alpha.kind === 'wave') return Math.min(1, Math.max(0, waveValue(alpha.func, alpha.base, alpha.amp, alpha.phase, alpha.freq, time)));
  return null;
}

/**
 * adjustColorsForFog (FinishShader): how a blended stage fades in a fog volume, as only the blends whose
 * contribution goes to nothing with the colour can: 1 the colour (GL_ONE GL_ONE, GL_ZERO
 * GL_ONE_MINUS_SRC_COLOR), 2 the alpha (GL_SRC_ALPHA GL_ONE_MINUS_SRC_ALPHA), 3 both (GL_ONE
 * GL_ONE_MINUS_SRC_ALPHA); 0 the others (a filter cannot be faded, an opaque one takes the fog pass)
 */
export function fogAdjust(src, dst) {
  const s = (src ?? '').toUpperCase(), d = (dst ?? '').toUpperCase();
  if ((s === 'GL_ONE' && d === 'GL_ONE') || (s === 'GL_ZERO' && d === 'GL_ONE_MINUS_SRC_COLOR')) return 1;
  if (s === 'GL_SRC_ALPHA' && d === 'GL_ONE_MINUS_SRC_ALPHA') return 2;
  if (s === 'GL_ONE' && d === 'GL_ONE_MINUS_SRC_ALPHA') return 3;
  return 0;
}
/** The same for the painters' blend names (a look's, a model's) */
export const BLEND_FACTORS = { add: ['GL_ONE', 'GL_ONE'], blend: ['GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA'], subtract: ['GL_ZERO', 'GL_ONE_MINUS_SRC_COLOR'], filter: ['GL_DST_COLOR', 'GL_ZERO'] };
export const blendFogAdjust = (mode) => (BLEND_FACTORS[mode] ? fogAdjust(...BLEND_FACTORS[mode]) : 0);

/** A stage's colour at a time: 1, or its wave clamped to 0..1 (RB_CalcWaveColor) */
export function stageBrightness(rgb, time) {
  if (rgb.kind !== 'wave') return 1;
  return Math.min(1, Math.max(0, waveValue(rgb.func, rgb.base, rgb.amp, rgb.phase, rgb.freq, time)));
}
